import { coverRequestsResponseSchema, type CoverRequestsResponse } from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";
import { refreshSessions } from "./use-groups.ts";

/**
 * Cover requests between staff (Pilates studio shape S3, migration 0072). One list for the reader,
 * as the API answers it: the owner and admins see every request, a staff member sees the ones they
 * asked, took, or can answer. Every write answers with that list again, so the screen shows the new
 * state at once; the sessions refresh too, because a cover can change who runs one.
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

/** GET /v1/business/businesses/:id/covers */
export function useCovers(businessId: string | null): UseQueryResult<CoverRequestsResponse> {
  return useQuery({
    queryKey: queryKeys.covers(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId!)}/covers`, coverRequestsResponseSchema),
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export type CoverAction =
  | { kind: "ask"; sessionId: string }
  | { kind: "take" | "decline" | "cancel"; coverId: string }
  | { kind: "decide"; coverId: string; decision: "approve" | "refuse" };

/** Ask, Take it, Not me, Cancel, Approve and Refuse: one mutation, the answer is the new list. */
export function useCoverAction(businessId: string): UseMutationResult<CoverRequestsResponse, unknown, CoverAction> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action) => {
      const at = base(businessId);
      switch (action.kind) {
        case "ask":
          return api.post(`${at}/sessions/${action.sessionId}/cover`, undefined, coverRequestsResponseSchema);
        case "decide":
          return api.post(`${at}/covers/${action.coverId}/decide`, { decision: action.decision }, coverRequestsResponseSchema);
        default:
          return api.post(`${at}/covers/${action.coverId}/${action.kind}`, undefined, coverRequestsResponseSchema);
      }
    },
    onSuccess: (list) => {
      queryClient.setQueryData(queryKeys.covers(businessId), list);
      refreshSessions(queryClient);
      // The owner To do counts covers that wait for approval.
      void queryClient.invalidateQueries({ queryKey: queryKeys.todo.owner(businessId) });
    },
    // Someone took it, or the request closed: read the list again so the screen shows why.
    onError: () => void queryClient.invalidateQueries({ queryKey: queryKeys.covers(businessId) }),
  });
}
